import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export const EmailTemplates = () => {
  const [templates, setTemplates] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [active, setActive] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/email-templates").then((r) => {
      setTemplates(r.data.templates); setDefaults(r.data.defaults);
      setActive(Object.keys(r.data.templates)[0]);
    }).catch(() => {});
  }, []);

  if (!templates) return null;
  const keys = Object.keys(templates);
  const t = templates[active];
  const setField = (field, val) => setTemplates({ ...templates, [active]: { ...t, [field]: val } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      keys.forEach((k) => { payload[k] = { subject: templates[k].subject, body: templates[k].body }; });
      await api.put("/email-templates", payload);
      toast.success("Email templates saved");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const resetActive = () => {
    const d = defaults[active];
    if (d) setTemplates({ ...templates, [active]: { ...t, subject: d.subject, body: d.body } });
  };

  const doPreview = async () => {
    try {
      const { data } = await api.post("/email-templates/preview", { subject: t.subject, body: t.body });
      setPreview(data);
    } catch (e) { toast.error("Preview failed"); }
  };

  const insertVar = (v) => setField("body", (t.body || "") + `{{${v}}}`);

  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm" data-testid="email-templates">
      <h3 className="font-head font-semibold mb-1 flex items-center gap-2"><Icons.FileText size={18} className="text-secondary" /> Email Templates</h3>
      <p className="text-xs text-muted-foreground mb-4">Edit the subject &amp; content for each automated email. The Grian header &amp; footer are added automatically. Use <code>{"{{variable}}"}</code> placeholders.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {keys.map((k) => (
          <button key={k} onClick={() => setActive(k)} data-testid={`tpl-tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${active === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            {templates[k].name}
          </button>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mb-3">{t.description}</p>

      <label className="label-caps">Subject</label>
      <input value={t.subject} onChange={(e) => setField("subject", e.target.value)} data-testid="tpl-subject"
        className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-input bg-background" />

      <label className="label-caps">Available variables (click to insert)</label>
      <div className="flex flex-wrap gap-1.5 mt-1 mb-3">
        {(t.variables || []).map((v) => (
          <button key={v} onClick={() => insertVar(v)} type="button"
            className="text-[11px] font-mono px-2 py-0.5 rounded bg-secondary/10 text-secondary hover:bg-secondary/20">{`{{${v}}}`}</button>
        ))}
      </div>

      <label className="label-caps">Body (HTML)</label>
      <textarea value={t.body} onChange={(e) => setField("body", e.target.value)} rows={12} data-testid="tpl-body"
        className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background font-mono text-xs leading-relaxed" />

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={doPreview} data-testid="tpl-preview" className="px-4 py-2 rounded-md border border-border text-sm font-medium flex items-center gap-1.5"><Icons.Eye size={15} /> Preview</button>
        <button onClick={resetActive} className="px-4 py-2 rounded-md border border-border text-sm text-muted-foreground">Reset this to default</button>
        <button onClick={save} disabled={saving} data-testid="tpl-save" className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm ml-auto">{saving ? "Saving…" : "Save Templates"}</button>
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-md w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="tpl-preview-modal">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Subject</p><p className="font-semibold">{preview.subject}</p></div>
              <button onClick={() => setPreview(null)}><Icons.X size={18} /></button>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="flex-1 w-full" style={{ minHeight: "60vh", border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
};
